import React from "react";
import clsx from "clsx";
import { Button } from "../button";
import { ToolTip } from "../tooltip";
import { ChevronDownIcon, ChevronUpIcon, PlusIcon } from "@heroicons/react/20/solid";

interface Column<T> {
  key: keyof T | string;
  header: string | React.ReactNode;
  render?: (item: T) => React.ReactNode;
  className?: string;
  isVisuallyPrimary?: boolean;
  sortable?: boolean;
  sortType?: "string" | "number" | "boolean";
  sortKey?: keyof T | string; // Optional custom sort key if different from display key
}

export interface Action<T> {
  label: string;
  icon?: React.ReactNode;
  onClick: (item: T) => void;
  srLabel?: (item: T) => string;
}

export type SortDirection = "asc" | "desc";

interface SortConfig {
  key: keyof any | string;
  direction: SortDirection;
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  actions?: Action<T>[];
  actionsLabel?: string;
  keyExtractor: (item: T) => string;
  heading?: string | React.ReactNode;
  description?: string;
  onAddItem?: () => void;
  addButtonLabel?: string;
  tableStyle?: TableStyle;
  tableDensity?: TableDensity;
  actionStyle?: ActionStyle;
  disabledRowIds?: string[] /* e.g. rows where a network request is in-flight (disable action buttons) */;
  className?: string;
  zeroState?: React.ReactNode /* what to display when no rows are available */;
  enableSorting?: boolean;
  defaultSortKey?: keyof T | string;
  defaultSortDirection?: SortDirection;
}

export const enum TableStyle {
  DEFAULT = "default",
  OUTLINED = "outlined" /* This is the "rounded corners with header background" style from Figma */,
}

export const enum TableDensity {
  DEFAULT = "default",
  COMPACT = "compact",
}

export const enum ActionStyle {
  DEFAULT = "default",
  STACKED = "stacked" /* Outlined buttons that stack vertically */,
  ICONS = "icons" /* Icon buttons that stack horizontally */,
}

const Table = <T,>({
  data,
  columns,
  actions,
  actionsLabel,
  keyExtractor,
  heading,
  description,
  onAddItem,
  addButtonLabel = "Add item",
  tableStyle = TableStyle.DEFAULT,
  actionStyle = ActionStyle.DEFAULT,
  tableDensity = TableDensity.DEFAULT,
  disabledRowIds = [],
  className,
  zeroState,
  enableSorting = false,
  defaultSortKey,
  defaultSortDirection = "desc",
}: TableProps<T>) => {
  const [sortConfig, setSortConfig] = React.useState<SortConfig | null>(() => {
    if (enableSorting && defaultSortKey) {
      return {
        key: defaultSortKey,
        direction: defaultSortDirection,
      };
    }
    return null;
  });

  const getValue = (item: T, key: keyof T | string): any => {
    if (typeof key === "string" && key.includes(".")) {
      // Handle nested keys like 'user.name'
      return key.split(".").reduce((obj: any, k: string) => obj?.[k], item);
    }
    return item[key as keyof T];
  };

  const sortedData = React.useMemo(() => {
    if (!enableSorting || !sortConfig) {
      return data;
    }

    const sortColumn = columns.find((col) => (col.sortKey || col.key) === sortConfig.key);

    if (!sortColumn) {
      return data;
    }

    const sortKey = sortColumn.sortKey || sortColumn.key;
    const sortType = sortColumn.sortType || "string";

    return [...data].sort((a, b) => {
      const aValue = getValue(a, sortKey);
      const bValue = getValue(b, sortKey);

      let comparison = 0;

      // Handle null/undefined values explicitly
      if ((aValue === null || aValue === undefined) && (bValue === null || bValue === undefined)) {
        comparison = 0;
      } else if (aValue === null || aValue === undefined) {
        comparison = 1;
      } else if (bValue === null || bValue === undefined) {
        comparison = -1;
      } else {
        // Primary sort based on data type
        switch (sortType) {
          case "number":
            comparison = Number(aValue) - Number(bValue);
            break;
          case "boolean":
            comparison = aValue === bValue ? 0 : aValue ? -1 : 1;
            break;
          case "string":
          default:
            comparison = String(aValue).localeCompare(String(bValue));
            break;
        }
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [data, sortConfig, columns, enableSorting]);

  const handleSort = (column: Column<T>) => {
    if (!enableSorting || !column.sortable) {
      return;
    }

    const sortKey = column.sortKey || column.key;

    setSortConfig((prevConfig) => {
      if (!prevConfig || prevConfig.key !== sortKey) {
        return { key: sortKey, direction: "desc" };
      }

      if (prevConfig.direction === "desc") {
        return { key: sortKey, direction: "asc" };
      }

      return null; // Clear sort
    });
  };

  const getSortIcon = (column: Column<T>) => {
    if (!enableSorting || !column.sortable) {
      return null;
    }

    const sortKey = column.sortKey || column.key;
    const isCurrentSort = sortConfig?.key === sortKey;

    if (!isCurrentSort) {
      return null;
    }

    return sortConfig.direction === "asc" ? (
      <ChevronUpIcon className="ml-1 w-4 h-4 text-gray-800" />
    ) : (
      <ChevronDownIcon className="ml-1 w-4 h-4 text-gray-800" />
    );
  };

  const addItemButton = onAddItem ? (
    <div className="mt-3">
      <Button onClick={onAddItem} color="tertiary" size="s">
        <PlusIcon className="mr-2 w-[20px] h-[20px] text-gray-500" />
        {addButtonLabel}
      </Button>
    </div>
  ) : null;

  return (
    <div className={clsx(className)}>
      {(heading || description) && (
        <div className="sm:flex sm:items-center">
          {(heading || description) && (
            <div className="sm:flex-auto">
              {heading && <h2 className="text-base font-semibold text-gray-900">{heading}</h2>}
              {description && <p className="mt-2 text-sm text-gray-700">{description}</p>}
            </div>
          )}
        </div>
      )}
      <div className={clsx("flow-root", tableStyle === TableStyle.DEFAULT ? "mt-6" : "mt-2")}>
        <div className="overflow-x-visible sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div
              className={clsx(
                tableStyle === TableStyle.OUTLINED && "rounded-[8px] border-1 border-gray-200",
              )}
            >
              <table className={"min-w-full divide-y divide-gray-300"}>
                <thead>
                  <tr className={clsx(tableStyle === TableStyle.OUTLINED && "bg-gray-50")}>
                    {columns.map((column) => (
                      <th
                        key={column.key.toString()}
                        scope="col"
                        className={clsx(
                          "text-left px-3",
                          tableDensity === TableDensity.COMPACT ? "py-2" : "py-3.5",
                          tableStyle === TableStyle.OUTLINED
                            ? "text-gray-500 font-medium uppercase text-sm"
                            : "text-gray-900 font-semibold",
                          column.className,
                          enableSorting &&
                            column.sortable &&
                            "cursor-pointer hover:bg-gray-100 select-none",
                        )}
                        style={
                          tableStyle === TableStyle.OUTLINED
                            ? {
                                letterSpacing: "5%",
                              }
                            : {}
                        }
                        onClick={() => handleSort(column)}
                      >
                        <div className="flex items-center">
                          {column.header}
                          {getSortIcon(column)}
                        </div>
                      </th>
                    ))}
                    {actions && actions.length > 0 && (
                      <th
                        scope="col"
                        className={clsx(
                          "relative text-center pr-4 sm:pr-0",
                          tableDensity !== TableDensity.COMPACT && "py-3.5",
                          tableStyle === TableStyle.OUTLINED
                            ? "text-gray-500 font-medium uppercase text-sm"
                            : "text-gray-900 font-semibold",
                        )}
                      >
                        {actionsLabel}
                        <span className="sr-only">{actionsLabel || "Actions"}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedData?.length > 0 ? (
                    sortedData.map((item) => {
                      const rowId = keyExtractor(item);
                      const isRowDisabled = disabledRowIds.includes(rowId);

                      return (
                        <tr key={rowId}>
                          {columns.map((column) => (
                            <td
                              key={column.key.toString()}
                              className={clsx(
                                "whitespace-nowrap text-sm px-3 text-gray-500",
                                tableDensity === TableDensity.COMPACT ? "py-1" : "py-4",
                                column.className,
                                column.isVisuallyPrimary && "text-gray-900 font-semibold",
                              )}
                            >
                              {column.render
                                ? column.render(item)
                                : String(item[column.key as keyof T] ?? "")}
                            </td>
                          ))}
                          {actions && actions.length > 0 && (
                            <td
                              className={clsx(
                                "whitespace-nowrap pr-2 pl-2",
                                tableDensity === TableDensity.COMPACT ? "py-1" : "py-4",
                                actionStyle === ActionStyle.STACKED &&
                                  "flex flex-col justify-around gap-y-1 items-stretch pt-1 pb-1",
                                actionStyle === ActionStyle.DEFAULT && "space-x-0",
                                actionStyle === ActionStyle.ICONS &&
                                  "space-x-1 flex justify-center",
                              )}
                            >
                              {actions.map((action, index) => {
                                const iconWithTooltip = (
                                  <ToolTip text={action.label}>{action.icon}</ToolTip>
                                );

                                return (
                                  <Button
                                    key={index}
                                    onClick={() => action.onClick(item)}
                                    color={
                                      actionStyle === ActionStyle.STACKED ||
                                      actionStyle === ActionStyle.ICONS
                                        ? "tertiary"
                                        : "link"
                                    }
                                    variant={
                                      (actionStyle === ActionStyle.ICONS && "round") || undefined
                                    }
                                    isCircular={actionStyle === ActionStyle.ICONS}
                                    disabled={isRowDisabled}
                                  >
                                    {actionStyle === ActionStyle.ICONS && iconWithTooltip}
                                    {actionStyle !== ActionStyle.ICONS && action.label}
                                    {action.srLabel && (
                                      <span className="sr-only">{action.srLabel(item)}</span>
                                    )}
                                  </Button>
                                );
                              })}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-4">
                        {zeroState || <p className="text-sm text-gray-500">No data available</p>}
                        {addItemButton}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {sortedData?.length > 0 && addItemButton}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Table;
